<template>
  <div v-if="authenticationLoading" class="auth-splash">
    <div class="loading-ring"></div>
    <strong>Ouverture de l’espace sécurisé…</strong>
  </div>
  <RouterView v-else-if="isPublicRoute" />
  <LoginView v-else-if="!currentUser" />
  <div v-else class="app-shell">
    <aside class="sidebar" :class="{ open: menuOpen }">
      <div class="brand" aria-label="Cotisation Section Lyon">
        <div class="brand-monogram" aria-hidden="true">
          <span class="brand-letter brand-letter-c">C</span>
          <span class="brand-letter brand-letter-s">S</span>
          <span class="brand-letter brand-letter-l">L</span>
        </div>
        <strong>Section Lyon</strong>
      </div>

      <nav>
        <div class="nav-group">
          <button
            class="nav-group-button"
            :class="{ active: isCotisationRoute }"
            :aria-expanded="cotisationOpen"
            @click="toggleNavigationGroup('cotisation')"
          >
            <i class="bi bi-wallet2"></i>
            <span>Cotisation mensuelle</span>
            <i
              class="bi nav-chevron"
              :class="cotisationOpen ? 'bi-chevron-up' : 'bi-chevron-down'"
            ></i>
          </button>
          <Transition name="nav-submenu">
            <div v-if="cotisationOpen" class="nav-submenu">
              <RouterLink
                v-for="item in cotisationNavigation"
                :key="item.to"
                :to="item.to"
                @click="menuOpen = false"
              >
                <i :class="item.icon"></i><span>{{ item.label }}</span>
              </RouterLink>
            </div>
          </Transition>
        </div>

        <div class="nav-group">
          <button
            class="nav-group-button"
            :class="{ active: isProjectRoute }"
            :aria-expanded="projectOpen"
            @click="toggleNavigationGroup('project')"
          >
            <i class="bi bi-collection"></i>
            <span>Projet</span>
            <i
              class="bi nav-chevron"
              :class="projectOpen ? 'bi-chevron-up' : 'bi-chevron-down'"
            ></i>
          </button>
          <Transition name="nav-submenu">
            <div v-if="projectOpen" class="nav-submenu">
              <RouterLink
                v-for="item in projectNavigation"
                :key="item.to"
                :to="item.to"
                @click="menuOpen = false"
              >
                <i :class="item.icon"></i><span>{{ item.label }}</span>
              </RouterLink>
            </div>
          </Transition>
        </div>

        <div class="nav-group">
          <button
            class="nav-group-button"
            :class="{ active: isAttendanceRoute }"
            :aria-expanded="attendanceOpen"
            @click="toggleNavigationGroup('attendance')"
          >
            <i class="bi bi-calendar-check"></i>
            <span>Liste de présence</span>
            <i
              class="bi nav-chevron"
              :class="attendanceOpen ? 'bi-chevron-up' : 'bi-chevron-down'"
            ></i>
          </button>
          <Transition name="nav-submenu">
            <div v-if="attendanceOpen" class="nav-submenu">
              <RouterLink
                v-for="item in attendanceNavigation"
                :key="item.to"
                :to="item.to"
                @click="menuOpen = false"
              >
                <i :class="item.icon"></i><span>{{ item.label }}</span>
              </RouterLink>
            </div>
          </Transition>
        </div>

        <RouterLink
          v-for="item in mainNavigation"
          :key="item.to"
          :to="item.to"
          class="nav-top-level"
          @click="menuOpen = false"
        >
          <i :class="item.icon"></i><span>{{ item.label }}</span>
        </RouterLink>
      </nav>

      <div class="sidebar-footer">
        <span class="status-dot"></span>
        <div>
          <strong>Données sécurisées</strong><small>Sauvegarde locale</small>
        </div>
      </div>
    </aside>

    <main>
      <header class="topbar">
        <button class="icon-button mobile-menu" @click="menuOpen = !menuOpen">
          <i class="bi bi-list"></i>
        </button>
        <div>
          <span class="eyebrow">Espace trésorerie</span>
          <h1>{{ currentTitle }}</h1>
        </div>
        <div class="topbar-actions">
          <div class="date-pill">
            <i class="bi bi-calendar3"></i>{{ currentDate }}
          </div>
          <div class="user-identity">
            <div class="avatar">{{ userInitials }}</div>
            <div>
              <strong>{{ currentUser.firstName }}</strong
              ><small>{{ currentUser.identifier }}</small>
            </div>
          </div>
          <button
            class="icon-button logout-button"
            title="Se déconnecter"
            @click="signOut"
          >
            <i class="bi bi-box-arrow-right"></i>
          </button>
        </div>
      </header>
      <RouterView />
    </main>
    <div v-if="menuOpen" class="overlay" @click="menuOpen = false"></div>
  </div>
  <ToastContainer />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import LoginView from "@/Domain/Authentication/LoginView.vue";
import ToastContainer from "@/Shared/components/ToastContainer.vue";
import {
  authenticationLoading,
  currentUser,
  initializeAuthentication,
  logout,
} from "@/Shared/services/authService";

const route = useRoute();
const menuOpen = ref(false);
const cotisationNavigation = [
  { to: "/", label: "Vue d’ensemble", icon: "bi bi-grid-1x2" },
  {
    to: "/cotisations",
    label: "Gestion des cotisations",
    icon: "bi bi-cash-coin",
  },
  { to: "/membres", label: "Membres", icon: "bi bi-people" },
  { to: "/finances", label: "Bilans & sorties", icon: "bi bi-bar-chart" },
  {
    to: "/bilan-mensuel",
    label: "Bilan par mois",
    icon: "bi bi-calendar3-range",
  },
  { to: "/bilan-personnes", label: "Bilan par personne", icon: "bi bi-table" },
  { to: "/analyse", label: "Analyse", icon: "bi bi-graph-up-arrow" },
  { to: "/mois", label: "Gestion des mois", icon: "bi bi-calendar2-week" },
  { to: "/rappels", label: "Rappels WhatsApp", icon: "bi bi-whatsapp" },
  {
    to: "/rappel-action-sociale",
    label: "Rappel action sociale",
    icon: "bi bi-heart-pulse",
  },
];
const mainNavigation = [
  {
    to: "/cotisations-ponctuelles",
    label: "Cotisation ponctuelle",
    icon: "bi bi-calendar2-plus",
  },
  { to: "/utilisateurs", label: "Utilisateurs", icon: "bi bi-person-lock" },
  { to: "/parametres", label: "Paramètres", icon: "bi bi-sliders2" },
];
const projectNavigation = [
  { to: "/projets", label: "Vue projets", icon: "bi bi-grid" },
  { to: "/projets/dara", label: "Dara", icon: "bi bi-journal-bookmark" },
  {
    to: "/projets/vente-viande",
    label: "Vente de viande",
    icon: "bi bi-basket",
  },
];
const attendanceNavigation = [
  { to: "/presences/analyse", label: "Analyse et suivi", icon: "bi bi-graph-up" },
  { to: "/presences/suivi", label: "Suivi des présences", icon: "bi bi-calendar-check" },
  { to: "/presences/membres", label: "Membres présence", icon: "bi bi-people" },
  { to: "/presences/rappels", label: "Rappels WhatsApp", icon: "bi bi-whatsapp" },
  { to: "/presences/qr-code", label: "QR code présence", icon: "bi bi-qr-code" },
];
const cotisationPaths = cotisationNavigation.map((item) => item.to);
const projectPaths = projectNavigation.map((item) => item.to);
const attendancePaths = attendanceNavigation.map((item) => item.to);
const isCotisationRoute = computed(() =>
  cotisationPaths.includes(route.path)
);
const isProjectRoute = computed(() => projectPaths.includes(route.path));
const isAttendanceRoute = computed(() => attendancePaths.includes(route.path));
const isPublicRoute = computed(() => Boolean(route.meta.public));
const cotisationOpen = ref(isCotisationRoute.value);
const projectOpen = ref(isProjectRoute.value);
const attendanceOpen = ref(isAttendanceRoute.value);
type NavigationGroup = "cotisation" | "project" | "attendance";
const currentTitle = computed(() =>
  String(route.meta.title || "Vue d’ensemble")
);
const userInitials = computed(() =>
  currentUser.value
    ? `${currentUser.value.firstName[0] || ""}${
        currentUser.value.lastName[0] || ""
      }`.toUpperCase()
    : ""
);
const currentDate = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
}).format(new Date());
async function signOut() {
  await logout();
}
function toggleNavigationGroup(group: NavigationGroup) {
  const shouldOpen =
    group === "cotisation"
      ? !cotisationOpen.value
      : group === "project"
      ? !projectOpen.value
      : !attendanceOpen.value;

  cotisationOpen.value = group === "cotisation" ? shouldOpen : false;
  projectOpen.value = group === "project" ? shouldOpen : false;
  attendanceOpen.value = group === "attendance" ? shouldOpen : false;
}
watch(
  () => route.path,
  () => {
    cotisationOpen.value = isCotisationRoute.value;
    projectOpen.value = isProjectRoute.value;
    attendanceOpen.value = isAttendanceRoute.value;
  }
);
onMounted(initializeAuthentication);
</script>

<style lang="scss">
@import "@/Shared/assets/app.scss";
</style>
